import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cognitiveData from "../../data/cognitive.json";
import styles from "./CognitiveCheckNew.module.css";
import { ArrowBack } from "@mui/icons-material";
import { db } from "../../utils/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getCognitiveTestSetId } from "../../utils/testId";
import { getUserId } from "../../utils/userId";

const testSteps = [
  { id: "test1", type: "digit_span" },
  { id: "test2", type: "random_cube" },
  { id: "test3", type: "verbal_learning_test" },
  { id: "test4", type: "tower_of_london" }
];

const CognitiveCheckNew: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [isLoading, setIsLoading] = useState(true);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [testResponse, setTestResponse] = useState<any[]>([]);
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const [hasPreviousTestHistory, setHasPreviousTestHistory] = useState(false);

  // 메시지 표시 상태
  const [showInitialMessage, setShowInitialMessage] = useState(false);
  const [showTest1Prompt, setShowTest1Prompt] = useState(false);
  const [showTest1Button, setShowTest1Button] = useState(false);
  const [showTest2Intro, setShowTest2Intro] = useState(false);
  const [showTest2Prompt, setShowTest2Prompt] = useState(false);
  const [showTest2Button, setShowTest2Button] = useState(false);
  const [showTest3Intro, setShowTest3Intro] = useState(false);
  const [showTest3Duration, setShowTest3Duration] = useState(false);
  const [showTest3Button, setShowTest3Button] = useState(false);
  const [showTest4Intro, setShowTest4Intro] = useState(false);
  const [showTest4Button, setShowTest4Button] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [showResultsButton, setShowResultsButton] = useState(false);
  const [scrollTargetReady, setScrollTargetReady] = useState(false);


  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadStepMessages = (index: number) => {
    // 이전 타임아웃 클리어
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    // 모든 상태 초기화
    setShowInitialMessage(false);
    setShowTest1Prompt(false);
    setShowTest1Button(false);
    setShowTest2Intro(false);
    setShowTest2Prompt(false);
    setShowTest2Button(false);
    setShowTest3Intro(false);
    setShowTest3Duration(false);
    setShowTest3Button(false);
    setShowTest4Intro(false);
    setShowTest4Button(false);
    setShowCompletionMessage(false);
    setShowResultsButton(false);

    let delay = 0; 

    // 초기 메시지 표시
    setShowInitialMessage(true);

    // 1. Test 1 (index 0)
    if (index > 0) {
      setShowTest1Prompt(true);
      setShowTest1Button(true);
    } else if (index === 0) {
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest1Prompt(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest1Button(true), delay));
    }

    // 2. Test 2 (index 1)
    if (index > 1) {
      setShowTest2Intro(true);
      setShowTest2Prompt(true);
      setShowTest2Button(true);
    } else if (index === 1) {
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest2Intro(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest2Prompt(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest2Button(true), delay));
    }

    // 3. Test 3 (index 2)
    if (index > 2) {
      setShowTest3Intro(true);
      setShowTest3Duration(true);
      setShowTest3Button(true);
    } else if (index === 2) {
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest3Intro(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest3Duration(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest3Button(true), delay));
    }

    // 4. Test 4 (index 3)
    if (index > 3) {
      setShowTest4Intro(true);
      setShowTest4Button(true);
      setScrollTargetReady(true);
    } else if (index === 3) {
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest4Intro(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowTest4Button(true), delay));

      delay += 100;
      timeoutsRef.current.push(setTimeout(() => setScrollTargetReady(true), delay));
    }

    // 5. Completion (index 4)
    if (index >= 4) {
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowCompletionMessage(true), delay));
      delay += 500;
      timeoutsRef.current.push(setTimeout(() => setShowResultsButton(true), delay));

      delay += 100;
      timeoutsRef.current.push(setTimeout(() => setScrollTargetReady(true), delay));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const userId = getUserId(); 
        if (!userId) {
          console.error("User ID is missing");
          navigate(`${prefix}/cognitive_check`);
          return;
        }

        const testSetId = getCognitiveTestSetId();
        if (!testSetId) {
          console.error('No test set ID found');
          navigate(`${prefix}/cognitive_check`);
          return;
        }

        const qCurrent = query(
          collection(db, 'userCognitiveResponse'),
          where('testSetId', '==', testSetId)
        );
        const querySnapshotCurrent = await getDocs(qCurrent);
        const results = querySnapshotCurrent.docs.map(doc => doc.data());
        
        const completedTests = results.map(result => result.testType);
        const nextTestIndex = completedTests.length;
        
        setCurrentTestIndex(nextTestIndex);
        setTestResponse(results);
        loadStepMessages(nextTestIndex);

        // --- B. 이전 완료된 테스트 세트 기록 확인 ---
        // 현재 테스트 세트 ID와 다른, 응답이 하나라도 있는 이전 기록을 찾습니다.
        const qPrevious = query(
          collection(db, 'userCognitiveResponse'),
          where("userId", "==", userId),
          where('testSetId', '!=', testSetId)
        );

        const querySnapshotPrevious = await getDocs(qPrevious);
        
        // 이전 테스트 세트 응답 기록이 하나라도 있다면(즉, 다른 testSetId가 있다면) true로 설정
        if (querySnapshotPrevious.docs.length > 0) {
            setHasPreviousTestHistory(true);
        }

      } catch (error) {
        console.error('Error loading test data:', error);
        navigate(`${prefix}/cognitive_check`);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, [navigate, prefix]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [
    showInitialMessage,
    showTest1Prompt,
    showTest1Button,
    showTest2Intro,
    showTest2Prompt,
    showTest2Button,
    showTest3Intro,
    showTest3Duration,
    showTest3Button,
    showTest4Intro,
    showTest4Button,
    showCompletionMessage,
    showResultsButton,
  ]);

  const handleBack = () => {
    const checkPagePath = `${prefix}/cognitive_check`; 
    const listPagePath = `${prefix}/check`;
    
    const shouldGoToCheckPage = testResponse.length > 0 || hasPreviousTestHistory;

    if (shouldGoToCheckPage) {
      navigate(checkPagePath);
    } else {
      navigate(listPagePath, { replace: true });
    }
  };

  const handleStartTest1 = () => {
    navigate(`${prefix}/cognitive/DigitSpan`);
  };

  const handleStartTest2 = () => {
    navigate(`${prefix}/cognitive/RandomCube`);
  };

  const handleStartTest3 = () => {
    navigate(`${prefix}/cognitive/VerbalLearning`);
  };

  const handleStartTest4 = () => {
    navigate(`${prefix}/cognitive/TowerOfLondon`);
  };

  const handleViewResults = () => {
    const sessionId = sessionStorage.getItem("current_session_id");

    navigate(`${prefix}/cognitive/check/results`, {
    state: {
      sessionId: sessionId // 세션 ID 전달
    }
  });
  };

  const isTestCompleted = (testId: string) => {
    return testResponse.some(response => response.testType === testId);
  };

  const renderTestStatusIcon = (testId: string, isActive: boolean) => {
    const completed = isTestCompleted(testId);
    if (completed) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="14" viewBox="0 0 19 14" fill="none">
          <path d="M1 5.52941L7.2963 12L18 1" stroke="#037DFF" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    } else if (isActive) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
          <path d="M18.5 10.134C19.1667 10.5189 19.1667 11.4811 18.5 11.866L2 21.3923C1.33333 21.7772 0.499999 21.2961 0.499999 20.5263L0.5 1.47372C0.5 0.703918 1.33333 0.222794 2 0.607694L18.5 10.134Z" fill="black"/>
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
          <path d="M18.5 10.134C19.1667 10.5189 19.1667 11.4811 18.5 11.866L2 21.3923C1.33333 21.7772 0.499999 21.2961 0.499999 20.5263L0.5 1.47372C0.5 0.703918 1.33333 0.222794 2 0.607694L18.5 10.134Z" fill="#CCCCCC"/>
        </svg>
      );
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_2")}</span>
      </div>

      <div className={styles.chatContainer}>
        {showInitialMessage && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test1_intro")}</div>
            </div>
          </div>
        )}

        {showTest1Prompt && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("chat_start_prompt")}</div>
            </div>
          </div>
        )}

        {showTest1Button && (
          <div className={styles.botMessage}>
            <button
              className={`${styles.testButton} ${isTestCompleted("test1") ? styles.completed : ''} ${currentTestIndex !== 0 ? styles.disabled : ''}`}
              onClick={currentTestIndex === 0 ? handleStartTest1 : undefined}
              disabled={currentTestIndex !== 0 || savingToFirebase}
            >
              {t("test1_button_text")}
              <div className={styles.testStatus}>
                {renderTestStatusIcon("test1", currentTestIndex === 0)}
              </div>
            </button>
          </div>
        )}

        {showTest2Intro && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test2_intro")}</div>
            </div>
          </div>
        )}

        {showTest2Prompt && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("chat_ready_prompt")}</div>
            </div>
          </div>
        )}

        {showTest2Button && (
          <div className={styles.botMessage}>
            <button
              className={`${styles.testButton} ${isTestCompleted("test2") ? styles.completed : ''} ${currentTestIndex !== 1 ? styles.disabled : ''}`}
              onClick={currentTestIndex === 1 ? handleStartTest2 : undefined}
              disabled={currentTestIndex !== 1 || savingToFirebase}
            >
              {t("test2_button_text")}
              <div className={styles.testStatus}>
                {renderTestStatusIcon("test2", currentTestIndex === 1)}
              </div>
            </button>
          </div>
        )}

        {showTest3Intro && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test3_intro")}</div>
            </div>
          </div>
        )}

        {showTest3Duration && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test3_duration_prompt")}</div>
            </div>
          </div>
        )}

        {showTest3Button && (
          <div className={styles.botMessage}>
            <button
              className={`${styles.testButton} ${isTestCompleted("test3") ? styles.completed : ''} ${currentTestIndex !== 2 ? styles.disabled : ''}`}
              onClick={currentTestIndex === 2 ? handleStartTest3 : undefined}
              disabled={currentTestIndex !== 2 || savingToFirebase}
            >
              {t("test3_button_text")}
              <div className={styles.testStatus}>
                {renderTestStatusIcon("test3", currentTestIndex === 2)}
              </div>
            </button>
          </div>
        )}

        {showTest4Intro && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test4_intro")}</div>
            </div>
          </div>
        )}

        {showTest4Button && (
          <div className={styles.botMessage}>
            <button
              className={`${styles.testButton} ${isTestCompleted("test4") ? styles.completed : ''} ${currentTestIndex !== 3 ? styles.disabled : ''}`}
              onClick={currentTestIndex === 3 ? handleStartTest4 : undefined}
              disabled={currentTestIndex !== 3 || savingToFirebase}
            >
              {t("test4_button_text")}
              <div className={styles.testStatus}>
                {renderTestStatusIcon("test4", currentTestIndex === 3)}
              </div>
            </button>
          </div>
        )}

        {showCompletionMessage && (
          <div className={styles.botMessage}>
            <div className={styles.messageContent}>
              <div className={styles.questionText}>{t("test_complete")}</div>
            </div>
          </div>
        )}

        {showResultsButton && (
          <div className={styles.botMessage}>
            <button
              className={styles.viewResultsButton}
              onClick={handleViewResults}
              disabled={savingToFirebase}
            >
              {savingToFirebase ? t("saving") : t("view_results")}
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default CognitiveCheckNew;